import { UploadSession } from "src/types/media";

/**
 * Chunked upload straight to GCS, with resume.
 *
 * What this replaces was a single signed PUT valid for one hour. An 18 GB
 * master on a 20 Mbit line takes about two, so large uploads could not finish
 * at all - and a dropped connection meant starting over. Here the file goes up
 * in pieces against a resumable session, and a refresh picks up where it left
 * off.
 *
 * Nothing passes through admin-api: it only opens and closes the session.
 */

/**
 * GCS rejects any non-final chunk that is not a multiple of this, and a
 * session that rejected a chunk is dead - it cannot be resumed, only started
 * again. Checked before the first byte for that reason.
 */
export const CHUNK_ALIGNMENT = 262144;

export interface UploadOptions {
  onProgress?: (uploadedBytes: number, totalBytes: number) => void;
  signal?: AbortSignal;
  /** Ask the server where it got to before sending anything. */
  resume?: boolean;
  /** localStorage key to clear once the upload completes. */
  resumeKey?: string;
  /** Injectable for tests. */
  fetch?: typeof globalThis.fetch;
}

/** Where a half-finished upload for one asset is remembered. */
export function resumeKeyFor(
  domain: string,
  entityIds: Array<number | string>,
  role: string
): string {
  return `panel.upload.${domain}.${entityIds.join(".")}.${role}`;
}

export function rememberSession(key: string, session: UploadSession, fileName: string): void {
  try {
    window.localStorage.setItem(key, JSON.stringify({ ...session, file_name: fileName }));
  } catch {
    // A browser with storage disabled simply loses the ability to resume.
  }
}

export function recallSession(
  key: string
): (UploadSession & { file_name?: string }) | null {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as UploadSession & { file_name?: string };
    // An expired session cannot be resumed; offering to would fail confusingly.
    if (new Date(parsed.expires_at).getTime() < Date.now()) {
      window.localStorage.removeItem(key);
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function forgetSession(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Nothing to do; the entry expires on its own.
  }
}

/**
 * Send `file` to `session.upload_url`, resuming if asked.
 *
 * Resolves once GCS has the whole object. Throws on a refused chunk, which
 * means the session is finished either way - the caller opens a new one.
 */
export async function uploadResumable(
  file: File,
  session: UploadSession,
  options: UploadOptions = {}
): Promise<void> {
  const {
    onProgress,
    signal,
    resume = false,
    resumeKey,
    fetch: doFetch = globalThis.fetch.bind(globalThis),
  } = options;

  const chunkBytes = session.chunk_bytes;

  if (chunkBytes % CHUNK_ALIGNMENT !== 0) {
    throw new Error(
      `chunk_bytes must be a multiple of ${CHUNK_ALIGNMENT}; got ${chunkBytes}`
    );
  }

  const total = file.size;
  let offset = resume ? await committedOffset(session.upload_url, total, doFetch) : 0;

  if (offset > 0) onProgress?.(offset, total);

  while (offset < total) {
    if (signal?.aborted) throw new Error("Upload aborted");

    const end = Math.min(offset + chunkBytes, total);
    const response = await doFetch(session.upload_url, {
      method: "PUT",
      headers: { "Content-Range": `bytes ${offset}-${end - 1}/${total}` },
      body: file.slice(offset, end),
      signal,
    });

    if (response.status === 200 || response.status === 201) {
      onProgress?.(total, total);
      if (resumeKey) forgetSession(resumeKey);

      return;
    }

    if (response.status !== 308) {
      throw new Error(`Upload refused with ${response.status}. Start a new upload.`);
    }

    // Trust the server's committed offset over our own arithmetic: it is what
    // a resume would read, and the two must not diverge.
    offset = rangeEnd(response.headers.get("Range")) ?? end;
    onProgress?.(offset, total);

    if (signal?.aborted) throw new Error("Upload aborted");
  }

  if (resumeKey) forgetSession(resumeKey);
}

/** Ask GCS how much of the object it already holds. */
async function committedOffset(
  uploadUrl: string,
  total: number,
  doFetch: typeof globalThis.fetch
): Promise<number> {
  const response = await doFetch(uploadUrl, {
    method: "PUT",
    // A status query carries no bytes, and says so with `*`.
    headers: { "Content-Range": `bytes */${total}` },
  });

  if (response.status === 200 || response.status === 201) return total;

  return rangeEnd(response.headers.get("Range")) ?? 0;
}

/** `bytes=0-16777215` means 16777216 bytes are committed. */
function rangeEnd(header: string | null): number | null {
  if (!header) return null;

  const match = /bytes=\d+-(\d+)/.exec(header);

  return match ? Number(match[1]) + 1 : null;
}
